import { useApiMutation, useApiQuery } from "@/hooks/use-api";
import { hasSelectedWorkspace } from "./core";

/**
 * Options for creating resource API hooks
 */
export interface CreateResourceApiOptions<T, TCreate = T, TUpdate = Partial<T>> {
  /**
   * The resource endpoint path (e.g., "dashboards", "trackers")
   */
  resourcePath: string;

  /**
   * Custom query parameters for the list endpoint
   */
  listParams?: Record<string, any>;

  /**
   * Custom query parameters for the single resource endpoint
   */
  singleParams?: Record<string, any>;

  /**
   * Whether to require workspace selection for queries
   */
  requireWorkspace?: boolean;

  /**
   * Transform the resource path for single resource queries
   * @param path The base resource path
   * @param id The resource ID
   * @returns The transformed path
   */
  transformSinglePath?: (path: string, id: string) => string;
}

/**
 * Generated API hooks for a resource
 */
export interface ResourceApiHooks<T, TCreate = T, TUpdate = Partial<T>> {
  /**
   * Hook to list all resources
   */
  useList: () => ReturnType<typeof useApiQuery<T[]>>;

  /**
   * Hook to get a single resource by ID
   */
  useOne: (id?: string) => ReturnType<typeof useApiQuery<T>>;

  /**
   * Hook to create a new resource
   */
  useCreate: () => ReturnType<typeof useApiMutation<T, TCreate>>;

  /**
   * Hook to update an existing resource
   */
  useUpdate: () => ReturnType<typeof useApiMutation<T, TUpdate, { id: string }>>;

  /**
   * Hook to delete a resource
   */
  useDelete: () => ReturnType<typeof useApiMutation<void, {}, { id: string }>>;
}

/**
 * Factory function to create standard CRUD API hooks for a resource.
 * 
 * @param options Configuration for the resource API
 * @returns Object containing all CRUD hooks for the resource
 * 
 * @example
 * ```typescript
 * const dashboardApi = createResourceApi<Dashboard, DashboardCreate>({
 *   resourcePath: "dashboards",
 *   requireWorkspace: true
 * });
 * 
 * // Use the generated hooks
 * const { data: dashboards } = dashboardApi.useList();
 * const { data: dashboard } = dashboardApi.useOne(dashboardId);
 * const createMutation = dashboardApi.useCreate();
 * const updateMutation = dashboardApi.useUpdate();
 * const deleteMutation = dashboardApi.useDelete();
 * ```
 */
export function createResourceApi<T, TCreate = T, TUpdate = Partial<T>>(
  options: CreateResourceApiOptions<T, TCreate, TUpdate>
): ResourceApiHooks<T, TCreate, TUpdate> {
  const {
    resourcePath,
    listParams = {},
    singleParams = {},
    requireWorkspace = true,
    transformSinglePath = (path, id) => `${path}/${id}`,
  } = options;

  return {
    useList: () =>
      useApiQuery<T[]>(
        {
          url: resourcePath,
          params: listParams
        },
        {
          enabled: requireWorkspace ? hasSelectedWorkspace() : true,
          staleTime: 30_000,
          gcTime: 5 * 60 * 1000,
        }
      ),

    useOne: (id?: string) =>
      useApiQuery<T>(
        {
          url: transformSinglePath(resourcePath, id || ""),
          params: singleParams
        },
        {
          enabled: !!id && (requireWorkspace ? hasSelectedWorkspace() : true),
          staleTime: 30_000,
          gcTime: 5 * 60 * 1000,
        }
      ),

    useCreate: () =>
      useApiMutation<T, TCreate>({
        url: resourcePath,
        method: "POST",
      }),

    useUpdate: () =>
      useApiMutation<T, TUpdate, { id: string }>({
        url: `${resourcePath}/{id}`,
        method: "PATCH",
      }),

    useDelete: () =>
      useApiMutation<void, {}, { id: string }>({
        url: `${resourcePath}/{id}`,
        method: "DELETE",
      }),
  };
}

/**
 * Extended options for creating resource API hooks with additional operations
 */
export interface CreateExtendedResourceApiOptions<T, TCreate = T, TUpdate = Partial<T>>
  extends CreateResourceApiOptions<T, TCreate, TUpdate> {
  /**
   * Additional custom endpoints to generate
   */
  customEndpoints?: {
    [key: string]: {
      url: string | ((id: string) => string);
      method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
      params?: Record<string, any>;
    };
  };
}

/**
 * Factory function to create extended API hooks with custom endpoints.
 * 
 * @param options Configuration for the resource API with custom endpoints
 * @returns Object containing all CRUD hooks plus custom endpoints
 * 
 * @example
 * ```typescript
 * const trackerApi = createExtendedResourceApi<Tracker, TrackerCreate>({
 *   resourcePath: "trackers",
 *   requireWorkspace: true,
 *   customEndpoints: {
 *     useRun: {
 *       url: (id) => `trackers/${id}/run`,
 *       method: "POST"
 *     },
 *     useExecutions: {
 *       url: (id) => `trackers/${id}/executions`,
 *       method: "GET"
 *     }
 *   }
 * });
 * ```
 */
export function createExtendedResourceApi<T, TCreate = T, TUpdate = Partial<T>>(
  options: CreateExtendedResourceApiOptions<T, TCreate, TUpdate>
): ResourceApiHooks<T, TCreate, TUpdate> & Record<string, any> {
  const baseHooks = createResourceApi<T, TCreate, TUpdate>(options);
  const { customEndpoints = {}, requireWorkspace = true } = options;

  const customHooks: Record<string, any> = {};

  for (const [hookName, config] of Object.entries(customEndpoints)) {
    if (config.method === "GET") {
      customHooks[hookName] = (id?: string) =>
        useApiQuery({
          url: typeof config.url === "function" ? config.url(id || "") : config.url,
          params: config.params,
        }, {
          enabled: !!id && (requireWorkspace ? hasSelectedWorkspace() : true)
        });
    } else {
      customHooks[hookName] = () =>
        useApiMutation({
          url: typeof config.url === "function" ? config.url("{id}") : config.url,
          method: config.method,
        });
    }
  }

  return { ...baseHooks, ...customHooks } as ResourceApiHooks<T, TCreate, TUpdate> & Record<string, any>;
}